import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Autocomplete, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useAlert } from "../../../../hooks/useAlert";
import "./AddCompany.css";
import { RequiredAsterisk } from "../../../UI/RequiredAsterisk/RequiredAsterisk";
import {
  useCreateCompanyMutation,
  useGetAllUsersCompanyQuery,
  useGetCompanyByIdQuery,
  useUpdateCompanyMutation,
} from "../../../../redux-toolkit/features/company/companyApiSlice";
import ProgressBar from "../../../UI/ProgressBar";
import { validateTaskSubmissionImageFile } from "../../../../utils/taskSubmissionImage";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  right: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: " 40px",
};

export const AddCompany = ({
  setCompanyOpen,
  companyOpen,
  companyId,
  setDrawer,
  setName,
  name,
  setCompanyAdmin,
  companyAdmin,
}) => {
  const { displayAlert } = useAlert();
  const [createCompany] = useCreateCompanyMutation();
  const isEditMode = Boolean(companyId && companyId !== 0 && companyId !== "");
  const { data: users } = useGetAllUsersCompanyQuery(companyId, {
    skip: !companyOpen || !isEditMode,
    refetchOnMountOrArgChange: true,
  });
  const { data } = useGetCompanyByIdQuery(
    { companyId },
    {
      skip: !companyOpen || !isEditMode,
      refetchOnMountOrArgChange: true,
    }
  );
  const [updateCompany] = useUpdateCompanyMutation();
  const [thumbnail, setThumbnail] = React.useState();
  const [isLarge, setIsLarge] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const handleClose = () => {
    setDrawer(0);
    setCompanyOpen(false);
    setThumbnail();
    setIsLarge(false);
  };
  React.useEffect(() => {
    setLoading(true);
    if (!isEditMode) {
      setThumbnail();
      setCompanyAdmin();
      setName();
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [companyId, isEditMode]);

  React.useEffect(() => {
    if (isEditMode && users && data) {
      const companyAdminData = data?.data?.companyAdmin;
      const companyAdminId =
        companyAdminData?._id || companyAdminData;
      const candidates = users.data?.users?.list || [];
      const matchedCandidate = companyAdminId
        ? candidates.find(
            (elm) => String(elm._id) === String(companyAdminId)
          )
        : null;
      // Prefer candidate match; fall back to populated company admin object
      const resolvedAdmin =
        matchedCandidate ||
        (companyAdminData?._id || companyAdminData?.email
          ? companyAdminData
          : null);
      setCompanyAdmin(resolvedAdmin || null);
      setName(data?.data.name);
      if (data?.data?.coverImage) {
        setThumbnail(data.data.coverImage);
      } else {
        setThumbnail();
      }
      setLoading(false);
    }
  }, [data, companyId, users, isEditMode]);

  const updateHandler = async () => {
    if (!name) {
      displayAlert({
        open: true,
        message: `All fields required!`,
        type: "error",
        timeout: "2000",
      });
    } else {
      const formData = new FormData();
      formData.append("name", name);
      formData.append(
        "companyAdmin",
        companyAdmin?._id ? String(companyAdmin._id) : ""
      );
      if (thumbnail instanceof File) {
        formData.append("coverImage", thumbnail);
      }
      const final = { formData, companyId };

      await updateCompany(final)
        .unwrap()
        .then(() => {
          displayAlert({
            open: true,
            message: `Updated Company Successfully!`,
            type: "success",
          });
          handleClose();
        })
        .catch((err) => {
          displayAlert({
            open: true,
            message: `${err?.data?.message}`,
            type: "error",
          });
        });
    }
  };

  const submitHandler = async () => {
    if (!name) {
      displayAlert({
        open: true,
        message: `All fields required!`,
        type: "error",
        timeout: "2000",
      });
    } else {
      const formData = new FormData();
      formData.append("name", name);
      if (thumbnail instanceof File) {
        formData.append("coverImage", thumbnail);
      }

      await createCompany(formData)
        .unwrap()
        .then(() => {
          displayAlert({
            open: true,
            message: `New Company Added Successfully!`,
            type: "success",
            timeout: "2000",
          });
          setCompanyAdmin();
          setName();
          setThumbnail();
          handleClose();
        })
        .catch((err) => {
          displayAlert({
            open: true,
            message: `${err?.data?.message}`,
            type: "error",
            timeout: "2000",
          });
        });
    }
  };
  const thumbnailHandler = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateTaskSubmissionImageFile(file);
    if (validationError) {
      setIsLarge(validationError.includes("1MB"));
      setThumbnail();
      event.target.value = "";
      displayAlert({
        open: true,
        message: validationError,
        type: "error",
        timeout: "2000",
      });
      return;
    }

    setIsLarge(false);
    setThumbnail(file);
  };


  return (
    <div>
      <Modal
        open={companyOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {isEditMode ? (
          <>
            {users ? (
              <Box sx={style}>
                <div className="create__formhead">
                  <h1>Update Company</h1>
                  <CloseIcon
                    onClick={handleClose}
                    sx={{
                      fontSize: "18px",
                      cursor: "pointer",
                      color: "var(--grey400)",
                      "&:hover": {
                        color: "var(--error)",
                      },
                    }}
                  />
                </div>
                <div className="seprator seprator__gap"></div>
                <div className="company__form">
                  <div>
                    <h5 className="ftwt__r">
                      Company Name <RequiredAsterisk />
                    </h5>

                    <TextField
                      sx={{
                        "& legend": { display: "none" },
                        "& fieldset": { top: 0 },
                        marginTop: "5px",
                      }}
                      required
                      id="outlined-basic"
                      variant="outlined"
                      fullWidth
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div>
                    <h5 className="ftwt__r">
                      Display Image
                    </h5>
                    <div
                      style={{ borderColor: isLarge ? "var(--error)" : "" }}
                      className="dropzone-wrapper"
                    >
                      {thumbnail ? (
                        <div className="dropzone-image">
                          <img
                            src={
                              !thumbnail.type
                                ? data?.data?.coverImage
                                : URL.createObjectURL(thumbnail)
                            }
                            alt="Thumbnail Preview"
                          />
                          <CloseIcon
                            onClick={() => setThumbnail()}
                            sx={{
                              fontSize: "18px",
                              cursor: "pointer",
                              color: "var(--grey400)",
                              "&:hover": {
                                color: "var(--error)",
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <div className="dropzone-desc">
                          <UploadFileOutlinedIcon sx={{ fontSize: "70px" }} />
                          <p
                            style={{
                              fontSize: "14px",
                            }}
                          >
                            Choose an image file{" "}
                            <span
                              style={{ fontSize: "11px", color: "var(--grey400)" }}
                            >
                              or drag it here.
                            </span>
                            <br />
                            <span
                              style={{ fontSize: "11px", color: "var(--grey400)" }}
                            >
                              Maximum file size 1MB
                            </span>
                          </p>
                        </div>
                      )}
                      {!thumbnail && (
                        <input
                          name="thumbnail"
                          onChange={thumbnailHandler}
                          type="file"
                          accept="image/*"
                          className="dropzone"
                        />
                      )}
                    </div>
                    {isLarge && (
                      <p
                        style={{
                          color: "var(--error)",
                          marginTop: "5px",
                          fontSize: "12px",
                        }}
                      >
                        Choose Image smaller than 1MB
                      </p>
                    )}
                  </div>
                  <div>
                    <h5 className="ftwt__r">Company Primary Admin</h5>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "var(--grey400)",
                        marginTop: "4px",
                      }}
                    >
                      Active admins in this company only
                    </p>
                    <Autocomplete
                      options={(() => {
                        const candidates = users?.data?.users?.list || [];
                        if (
                          companyAdmin?._id &&
                          !candidates.some(
                            (opt) =>
                              String(opt._id) === String(companyAdmin._id)
                          )
                        ) {
                          return [companyAdmin, ...candidates];
                        }
                        return candidates;
                      })()}
                      isOptionEqualToValue={(option, value) =>
                        String(option?._id) === String(value?._id)
                      }
                      getOptionLabel={(option) => option?.email || ""}
                      fullWidth
                      onChange={(event, newValue) => {
                        setCompanyAdmin(newValue);
                      }}
                      name="companyAdmin"
                      value={companyAdmin || null}
                      renderInput={(params) => (
                        <TextField
                          sx={{
                            "& legend": { display: "none" },
                            "& fieldset": { top: 0 },
                            marginTop: "5px",
                            borderRadius: "2px",
                          }}
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="company__actions">
                    <Button
                      onClick={updateHandler}
                      sx={{ width: "100px" }}
                      variant="contained"
                    >
                      Update
                    </Button>
                  </div>
                </div>
              </Box>
            ) : (
              <Box sx={style}>
                <ProgressBar />
              </Box>
            )}
          </>
        ) : loading ? (<Box sx={style}>
          <ProgressBar />
        </Box>) : (
          <Box sx={style}>
            <div className="create__formhead">
              <h1>Add Company</h1>
              <CloseIcon
                onClick={handleClose}
                sx={{
                  fontSize: "18px",
                  cursor: "pointer",
                  color: "var(--grey400)",
                  "&:hover": {
                    color: "var(--error)",
                  },
                }}
              />
            </div>
            <div className="seprator seprator__gap"></div>
            <div className="company__form">
              <div>
                <h5 className="ftwt__r">
                  Company Name <RequiredAsterisk />
                </h5>

                <TextField
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    marginTop: "5px",
                  }}
                  required
                  id="outlined-basic"
                  variant="outlined"
                  fullWidth
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <h5 className="ftwt__r">
                  Display Image
                </h5>
                <div
                  style={{ borderColor: isLarge ? "var(--error)" : "" }}
                  className="dropzone-wrapper"
                >
                  {thumbnail ? (
                    <div className="dropzone-image">
                      <img
                        src={
                          !thumbnail.type
                            ? data?.data?.coverImage
                            : URL.createObjectURL(thumbnail)
                        }
                        alt="Thumbnail Preview"
                      />
                      <CloseIcon
                        onClick={() => setThumbnail()}
                        sx={{
                          fontSize: "18px",
                          cursor: "pointer",
                          color: "var(--grey400)",
                          "&:hover": {
                            color: "var(--error)",
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className="dropzone-desc">
                      <UploadFileOutlinedIcon sx={{ fontSize: "70px" }} />
                      <p
                        style={{
                          fontSize: "14px",
                        }}
                      >
                        Choose an image file{" "}
                        <span
                          style={{ fontSize: "11px", color: "var(--grey400)" }}
                        >
                          or drag it here.
                        </span>
                        <br />
                        <span
                          style={{ fontSize: "11px", color: "var(--grey400)" }}
                        >
                          Maximum file size 1MB
                        </span>
                      </p>
                    </div>
                  )}
                  {!thumbnail && (
                    <input
                      name="thumbnail"
                      onChange={thumbnailHandler}
                      type="file"
                      accept="image/*"
                      className="dropzone"
                    />
                  )}
                </div>
                {isLarge && (
                  <p
                    style={{
                      color: "var(--error)",
                      marginTop: "5px",
                      fontSize: "12px",
                    }}
                  >
                    Choose Image smaller than 1MB
                  </p>
                )}
              </div>
              <div>
                <h5 className="ftwt__r">Company Primary Admin</h5>
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--grey400)",
                    marginTop: "4px",
                  }}
                >
                  Assign an active admin after creating the company and granting
                  Admin access.
                </p>
              </div>
              <div className="company__actions">
                <Button
                  onClick={submitHandler}
                  sx={{ width: "100px" }}
                  variant="contained"
                >
                  Create
                </Button>
              </div>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};
